import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useDesignContext } from '../../hooks/useDesignContext';
import { getGraniteForColor } from '../../utils/colorMappings';
import { buildApiUrl } from '../../config/api';



const fitAndCenter = (el: any, pad: number = 60): any => {
  const W = 1200;
  const H = 600;
  const availW = Math.max(0, W - pad * 2);
  const availH = Math.max(0, H - pad * 2);

  if (el.width <= 0 || el.height <= 0) {
    return {
      ...el,
      x: Math.max(0, (W - el.width) / 2),
      y: Math.max(0, (H - el.height) / 2)
    };
  }

  const scale = Math.min(availW / el.width, availH / el.height);
  const width = Math.floor(el.width * scale);
  const height = Math.floor(el.height * scale);
  const x = Math.floor((W - width) / 2);
  const y = Math.floor((H - height) / 2);

  return { ...el, x, y, width, height };
};

interface ColorsProps {
  selectedColorId: number | null;
  selectedColorIndex: number;
  // Returns a boolean: true if the color change was accepted

  onColorSelect: (colorId: number, colorIndex: number) => Promise<boolean> | boolean;
  setForcedGranite?: (granite: string | null) => void;
}

const Colors: React.FC<ColorsProps> = ({
  selectedColorId,
  selectedColorIndex,
  onColorSelect,
  setForcedGranite
}) => {
  const {
    currentDesign,
    updateDesign,
    incrementTemplateResetKey,
    setIsTemplateSelected,
    setSelectedGraniteImage,
    setSelectedGraniteBaseImage,
    selectedGraniteMeta,
    isPanelPreloaderActive,
    sharedColors,
    isGlobalLoading,
    openModal,
    fetchModelDetails,
    fetchDesignPreview,
    designMode
  } = useDesignContext();

  // Guard against race conditions when switching colors rapidly within Colors panel
  const colorReqIdRef = React.useRef(0);

  // When granite color changes, update template elements' colorId promptly.

  useEffect(() => {
    if (selectedColorId == null) return;
    updateDesign(prev => {
      const elements = (prev.elements || []).map(el => {
        if (el.type !== 'artwork') return el;
        const role = (el.properties?.role || '').toString().toLowerCase();
        if (role === 'template' || el.properties?.isTemplate) {
          return { ...el, properties: { ...el.properties, colorId: selectedColorId } };
        }
        return el;
      });
      return { ...prev, elements };
    });
  }, [selectedColorId, updateDesign]);

  const [colors, setColors] = useState<any[]>(sharedColors);
  const [modelDetails, setModelDetails] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(isGlobalLoading && sharedColors.length === 0);
  const [isAvailabilityLoading, setIsAvailabilityLoading] = useState<boolean>(false);
  const [availabilityByColor, setAvailabilityByColor] = useState<Record<number, number>>({});

  // Sync colors from shared context
  useEffect(() => {
    if (sharedColors.length > 0) {
      setColors(sharedColors);
      setIsLoading(false);
    }
  }, [sharedColors]);




  // Memoized function to check if element is a template
  const isTemplateElement = useCallback((el: any): boolean => {
    if (el.type !== 'artwork') return false;

    const props = el.properties || {};
    return props.role === 'template' ||
      props.isTemplate === true ||
      props.template === true ||
      el.id === 'preview-image' ||
      el.id?.startsWith('template-');
  }, []);

  // Global cache for API results to avoid redundant fetches across re-renders
  const apiCacheRef = useRef<Record<string, any>>({});
  const availabilityCacheRef = useRef<Record<string, any>>({});
  const inFlightAvailabilityRef = useRef<Record<string, Promise<any> | undefined>>({});

  // Global fetchColors removed - now handled by DesignProvider sharedColors

  // Fetch model details when model changes
  useEffect(() => {
    const modelId = currentDesign.selectedModelId;
    if (!modelId) return;

    const controller = new AbortController();

    const fetchModelDetailsLocal = async () => {
      try {
        const details = await fetchModelDetails(modelId);
        setModelDetails(details);
      } catch (err: any) {
        if (err?.name !== 'AbortError') {
          // Fallback details
          const fallbackDetails = {
            sizes: [{
              die_size: '36x8x24',
              base_size: '48x12x8',
              size_type: 'single'
            }],
            colors: [{
              color_name: 'Black'
            }]
          };
          setModelDetails(fallbackDetails);
        }
      }
    };

    // Delay model details fetch until a modal (eg. Models, Colors) is opened.
    // This removes the initial "details" request on boot.
    if (!openModal) return;

    fetchModelDetailsLocal();

    return () => controller.abort();
  }, [currentDesign.selectedModelId, openModal]);

  // Recompute color availability when model or size changes
  useEffect(() => {
    const modelId = currentDesign.selectedModelId;
    const sizeId = currentDesign.selectedSizeId;

    if (!modelId) {
      setIsAvailabilityLoading(false);
      return;
    }

    const cacheKey = `${modelId}_${sizeId || 0}`;

    // Return immediately from cache if available
    if (availabilityCacheRef.current[cacheKey]) {
      setAvailabilityByColor(availabilityCacheRef.current[cacheKey]);
      setIsAvailabilityLoading(false);
      return;
    }

    const controller = new AbortController();

    // Small debounce mirrors the background effect — absorbs back-to-back
    // state updates (e.g. modelId fires then sizeId fires 1 tick later).
    const timer = setTimeout(async () => {
      if (controller.signal.aborted) return;

      // Deduplicate: if an identical request is in-flight, wait for it
      if (inFlightAvailabilityRef.current[cacheKey]) {
        try {
          const data = await inFlightAvailabilityRef.current[cacheKey];
          if (!controller.signal.aborted && data.ok) {
            setAvailabilityByColor(data.availability);
          }
        } catch { }
        setIsAvailabilityLoading(false);
        return;
      }

      setIsAvailabilityLoading(true);

      const promise = (async () => {
        try {
          const url = buildApiUrl(`/api/designs/availability?model_id=${modelId}&size_id=${sizeId || 0}`);
          const res = await fetch(url, { signal: controller.signal });
          if (!res.ok) throw new Error('Failed to fetch availability');
          const data = await res.json();
          if (data.ok) {
            availabilityCacheRef.current[cacheKey] = data.availability;
          }
          return data;
        } finally {
          delete inFlightAvailabilityRef.current[cacheKey];
        }
      })();

      inFlightAvailabilityRef.current[cacheKey] = promise;

      try {
        const data = await promise;
        if (!controller.signal.aborted && data.ok) {
          setAvailabilityByColor(data.availability);
        }
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          console.error('Availability check failed:', err);
        }
      } finally {
        setIsAvailabilityLoading(false);
      }
    }, 80);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [currentDesign.selectedModelId, currentDesign.selectedSizeId]);

  // Clear all template elements
  const clearAllTemplates = useCallback(async () => {
    updateDesign(prev => ({
      ...prev,
      elements: prev.elements.filter(el => !isTemplateElement(el))
    }));

    await new Promise(resolve => setTimeout(resolve, 50));
  }, [updateDesign, isTemplateElement]);

  // Fetch preview image for selected color
  const fetchPreviewImage = useCallback(async (colorIndex: number) => {
    const reqId = ++colorReqIdRef.current;
    try { setSelectedGraniteBaseImage && setSelectedGraniteBaseImage(null); } catch { }
    if (!modelDetails || !currentDesign.selectedModelId) return;

    // Clear existing template elements first
    updateDesign(prev => {
      const nonTemplateElements = prev.elements.filter(el => {
        if (el.type !== 'artwork') return true;

        const props = el.properties || {};
        const isTemplate = props.role === 'template' ||
          props.isTemplate === true ||
          props.template === true ||
          el.id === 'preview-image' ||
          el.id.startsWith('template-') ||
          el.id.includes('template') ||
          el.id.includes('preview');

        return !isTemplate;
      });

      return { ...prev, elements: nonTemplateElements };
    });

    // Small delay to ensure DOM is updated
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      const selectedColor = colors[colorIndex];
      const colorId = selectedColor?.id;

      if (!colorId) {
        return;
      }

      const sizes = modelDetails?.sizes || [];
      const selectedSize = sizes[0];
      const sizeId = selectedSize?.id;

      if (!sizeId) {
        return;
      }

      // Use shared fetchDesignPreview for caching and deduplication
      const data = await fetchDesignPreview(currentDesign.selectedModelId!, sizeId, colorId);

      if (data.status === 'success' && (data.data.model_img || data.data.preview_png)) {
        let imageUrl = '';

        // Prefer preview image (colored) over model_img
        if (data.data.preview_api_url &&
          data.data.preview_api_url !== buildApiUrl('/api/uploads/')) {
          imageUrl = data.data.preview_api_url;
        } else if (data.data.preview_png) {
          const cleanPath = String(data.data.preview_png).replace(/^\/*uploads\//, '');
          imageUrl = buildApiUrl(`/api/uploads/${cleanPath}`);
        } else if (data.data.model_img_api_url && data.data.model_img_api_url !== buildApiUrl('/api/uploads/')) {
          imageUrl = data.data.model_img_api_url;
        } else if (data.data.model_img) {
          const cleanModel = String(data.data.model_img).replace(/^\/*uploads\//, '');
          imageUrl = buildApiUrl(`/api/uploads/${cleanModel}`);
        }

        // Set or clear base image for this color (guarded by latest request)
        try {
          const baseApi = data?.data?.preview_img_api_url;
          const baseRaw = data?.data?.preview_img;
          const baseUrl = baseApi && baseApi !== buildApiUrl('/api/uploads/')
            ? baseApi
            : (baseRaw ? buildApiUrl(`/api/uploads/${String(baseRaw).replace(/^\/*uploads\//, '')}`) : null);
          if (setSelectedGraniteBaseImage && colorReqIdRef.current === reqId) {
            if (baseUrl) setSelectedGraniteBaseImage(baseUrl);
            else setSelectedGraniteBaseImage(null);
          }
        } catch { }

        if (!imageUrl) {
          return;
        }

        await new Promise<void>((resolve) => {
          const img = new window.Image();

          img.onload = () => {
            try {
              const templateElement = {
                id: 'main-template',
                type: 'artwork',
                x: 0,
                y: 0,
                width: img.naturalWidth || img.width,
                height: img.naturalHeight || img.height,
                rotation: 0,
                properties: {
                  src: imageUrl,
                  alt: 'Monument Preview',
                  image: img,
                  role: 'template',
                  isTemplate: true,
                  template: true,
                  draggable: false,
                  colorId: selectedColor?.id,
                  colorName: selectedColor?.name || selectedColor?.code
                }
              };

              const baseElements = [templateElement];

              // Process artwork elements from model details
              const artworkElements = Array.isArray(modelDetails?.artworks)
                ? (modelDetails.artworks as any[]).map((a: any) => ({
                  id: `artwork-${a.id}`,
                  type: 'artwork',
                  x: Number(a.x) || 0,
                  y: Number(a.y) || 0,
                  width: (a.width != null ? Number(a.width) : 0) || 0,
                  height: (a.height != null ? Number(a.height) : 0) || 0,
                  rotation: Number(a.rotation) || 0,
                  properties: {
                    src: a.file_path,
                    alt: a.name || 'Artwork',
                    draggable: !(a.is_required === 1 || a.is_required === true)
                  }
                }))
                : [];

              updateDesign(prev => {
                // Filter out existing template elements
                const nonTemplateElements = prev.elements.filter(el => {
                  if (el.type !== 'artwork') return true;

                  const props = el.properties || {};
                  const isTemplate = props.role === 'template' ||
                    props.isTemplate === true ||
                    props.template === true ||
                    el.id === 'preview-image' ||
                    el.id.startsWith('template-');

                  return !isTemplate;
                });

                const newElements = [...nonTemplateElements, ...baseElements, ...artworkElements];

                // Center text elements relative to the template container
                const container = baseElements.find(el =>
                  el.type === 'artwork' &&
                  (el.properties as any)?.role === 'template'
                );

                if (container) {
                  const textElements = newElements
                    .filter(el => el.type === 'text')
                    .map(el => {
                      const boxWidth = (el as any).width || 800;
                      const centerX = (container.x || 0) + (container.width || 0) / 2;
                      const newX = Math.floor(centerX - boxWidth / 2);

                      return {
                        ...el,
                        x: newX,
                        properties: {
                          ...el.properties,
                          align: 'center'
                        }
                      } as any;
                    });

                  const nonTextElements = newElements.filter(el => el.type !== 'text');
                  return {
                    ...prev,
                    elements: [...nonTextElements, ...textElements]
                  };
                }

                return { ...prev, elements: newElements };
              });
            } catch (error) {
            } finally {
              resolve();
            }
          };

          img.onerror = () => {
            resolve();
          };

          img.src = imageUrl;
        });
      }
    } catch (error) {
      // Do not clear base image on errors; prevents flicker
    }
  }, [modelDetails, currentDesign.selectedModelId, colors, updateDesign, fetchDesignPreview]);

  // Throttle rapid repeated clicks to avoid overlapping async work
  const lastColorClickRef = React.useRef<number>(0);

  // Handle color selection
  const handleColorSelection = useCallback(async (selectedColor: any, colorIndex: number) => {
    const colorId = selectedColor.id;
    const now = Date.now();
    
    // Debounce rapid clicks
    if (now - (lastColorClickRef.current || 0) < 250) {
      return;
    }

    // Double check availability (safety check against race conditions)
    if (currentDesign.selectedModelId) {
      const availability = availabilityByColor[colorId];
      if (availability === 0 || (isAvailabilityLoading && availability === undefined) || isPanelPreloaderActive) {
        return;
      }
    }

    lastColorClickRef.current = now;

    // If the same color is already selected, don't do anything
    if (selectedColorId === colorId) {
      return;
    }

    // Delegate applying the color (and any template changes) to the parent
    const accepted = await Promise.resolve(onColorSelect(colorId, colorIndex));
    if (!accepted) {
      return; // Parent deferred or cancelled application
    }

    // Correctly derive the granite type from the selected color's name/ID
    const granite = getGraniteForColor(colorId, selectedColor.name);
    if (setForcedGranite) setForcedGranite(granite || null);
  }, [
    selectedColorId,
    onColorSelect,
    setForcedGranite,
    availabilityByColor,
    isAvailabilityLoading,
    isPanelPreloaderActive,
    currentDesign.selectedModelId
  ]);

  // Granite color palette fallback
  const granitePalette: { name: string; hex: string }[] = [
    { name: 'Premium Jet Black', hex: '#000000' },
    { name: 'Medium Gray', hex: '#8a8a8a' }
  ];

  // Use fetched colors or fallback to granite palette
  const rawColors = colors.length > 0
    ? colors
    : granitePalette.map((g, i) => ({
      id: i + 1,
      code: g.name.toLowerCase(),
      name: g.name,
      hex_hint: g.hex,
      image_path: null
    }));

  // Sort so Black/PJB is always on top, Grey/Gray always below
  const colorSortOrder = (item: any): number => {
    const code = String(item.code || '').toUpperCase();
    const name = String(item.name || '').toUpperCase();
    const id = Number(item.id);
    if (code.includes('PJB') || name.includes('BLACK') || id === 5) return 0;
    if (code === 'MG' || code.includes('GREY') || code.includes('GRAY') || name.includes('GRAY') || name.includes('GREY') || id === 12) return 1;
    return 2;
  };
  const displayColors = [...rawColors].sort((a, b) => colorSortOrder(a) - colorSortOrder(b));

  return (
    <ColorsContainer>
      <ColorsTitle>
        Colors
      </ColorsTitle>

      <ColorsGrid>

        {displayColors.map((item: any, idx: number) => {
          const selectedIdNum = selectedColorId != null ? Number(selectedColorId) : null;
          const itemIdNum = item?.id != null ? Number(item.id) : null;
          const isSelected = selectedIdNum != null && itemIdNum != null && selectedIdNum === itemIdNum;
          const availability = availabilityByColor[itemIdNum as number];
          // Disable if: explicitly unavailable (is_active=0), OR panel preloader is active
          // We allow clicking while "checking" (undefined) so we don't flash disabled states, 
          // but the handler will block it until loaded if needed.
          // Disable only if: standard mode AND explicitly unavailable (is_active=0)
          const isDisabled = designMode !== 'ESQ' && currentDesign.selectedModelId
            ? (availability === 0 || isPanelPreloaderActive)
            : false;

          const bgColor = itemIdNum === 12 ? '#8a8a8a' : (itemIdNum === 5 ? '#000000' : (item.hex_hint || '#cccccc'));

          const code = String(item.code || '').toUpperCase();
          const name = String(item.name || '').toUpperCase();
          const isPJB = code.includes('PJB') || name.includes('BLACK') || itemIdNum === 5;
          const isGrey = code === 'MG' || code.includes('GREY') || code.includes('GRAY') || name.includes('GRAY') || name.includes('GREY') || itemIdNum === 12;
          const bgImage = isPJB
            ? '/landing/pjb_20x10.webp'
            : isGrey
              ? '/landing/grey_20x10.webp'
              : null;

          return (
            <ColorItem
              key={item.id || idx}
              $isSelected={Boolean(isSelected)}
              $isDisabled={Boolean(isDisabled)}
              $bgColor={bgColor}
              $bgImage={bgImage}
              title={isDisabled ? `${item.name} (Unavailable)` : item.name}
              onClick={() => {
                // TRIPLE CHECK: Block at the click handler level too
                const isBlocked = isPanelPreloaderActive || (currentDesign.selectedModelId && availability === 0);

                if (isBlocked) {
                  return;
                }
                const sharedIndex = sharedColors.findIndex((c: any) => Number(c.id) === Number(item.id));
                handleColorSelection(item, sharedIndex >= 0 ? sharedIndex : idx);
              }}
            >

              {isSelected && (
                <SelectionIndicator>
                  <div className="inner-dot" />
                </SelectionIndicator>
              )}
            </ColorItem>
          );
        })}
      </ColorsGrid>
    </ColorsContainer>
  );
};

// Styled components
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const ColorsContainer = styled.div`
  background: #bccddf;
  padding: 8px 6px;
  border-radius: 8px;
  width: 70px;
  margin: 0 auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    flex-direction: column-reverse;
    width: 70px;
    height: 56px;
    align-items: center;
    justify-content: center;
    padding: 6px 4px;
    gap: 2px;
    border-radius: 5px;
  }
`;

const ColorsTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #2c3e50;
  text-align: center;
  margin-bottom: 2px;

  @media (max-width: 768px) {
    margin-bottom: 0;
    font-size: 11px;
    font-weight: 600;
    color: #000000;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(188, 205, 223, 0.4);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
`;

const MiniSpinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top: 2px solid #5D722C;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
`;

const ColorsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: relative;
  min-height: 70px;

  @media (max-width: 768px) {
    flex-direction: row;
    min-height: auto;
    gap: 4px;
  }
`;

const ColorItem = styled.div<{ $isSelected: boolean; $isDisabled?: boolean; $bgColor?: string; $bgImage?: string | null }>`
  width: 100%;
  height: 32px;
  border-radius: 6px;
  overflow: hidden;
  border: ${props => props.$isSelected ? '3px solid #5D722C' : '1px solid #cdd5de'};
  position: relative;
  cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  background: ${props => props.$bgImage ? `url(${props.$bgImage}) center / cover no-repeat` : (props.$bgColor || '#ffffff')};
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.1);
  filter: ${props => props.$isDisabled ? 'grayscale(0.9)' : 'none'};
  opacity: ${props => props.$isDisabled ? 0.35 : 1};
  
  &:hover {
    transform: ${props => (props.$isSelected || props.$isDisabled) ? 'none' : 'scale(1.02)'};
  }

  @media (max-width: 768px) {
    width: 22px;
    height: 22px;
    border-radius: 4px;
    border: ${props => props.$isSelected ? '2px solid #5D722C' : '1px solid #cdd5de'};
  }
`;


const SelectionIndicator = styled.div`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 12px;
  height: 12px;
  background-color: #5D722C;
  border-radius: 50%;
  border: 1.5px solid white;
  display: flex;
  align-items: center;
  justify-content: center;

  .inner-dot {
    width: 4px;
    height: 4px;
    background: white;
    border-radius: 50%;
  }

  @media (max-width: 768px) {
    top: 1px;
    right: 1px;
    width: 8px;
    height: 8px;
    border-width: 1.2px;

    .inner-dot {
      width: 2px;
      height: 2px;
    }
  }
`;

export default Colors;